/**
 * @docit/editor-core — PUBLIC API. THE ONLY public surface of the v2 editor engine.
 *
 * HARD BOUNDARY (AC 14.2): no `prosemirror-*` or `yjs` type may appear in anything exported
 * here. The engine is swappable; a leak is a build failure enforced by
 * `scripts/check-surface.mjs`. Keep this file free of those identifiers.
 */

// ── Dual-ACL gateways ──
export type { CapabilityGateway, ResourceAclGateway, ResourceAction } from "./acl/gateways";
export { passesDualAcl } from "./acl/gateways";
// ── Derivation + ink boundaries ──
export type { CommittedSpan, DerivedCard, Deriver } from "./deriver/Deriver";
export { NoopDeriver } from "./deriver/Deriver";
export type { EngineHandle } from "./engine/handle";
export type { InkLayer, InkPoint, InkStroke } from "./ink/InkLayer";
// ── Lane resolution + safety floor ──
export type { LanePolicy, LaneResolver } from "./lanes/LaneResolver";
export { DefaultLaneResolver, mostRestrictive } from "./lanes/LaneResolver";

// ── Live-object registry ──
export type { LiveObjectDefinition } from "./nodes/registry";
export { getLiveObject, listLiveObjects, registerLiveObject } from "./nodes/registry";
// ── Single write path ──
export type { ApplyDeps } from "./transactions/apply";
export { acceptProposal, applyEdit } from "./transactions/apply";
// ── Plain contracts ──
export type {
  ApplyResult,
  Attribution,
  BlockType,
  Commitment,
  CommitmentState,
  DocRange,
  EditIntent,
  InlineFormat,
  Lane,
  ListType,
  ObjectActionRequest,
  ParticipantRef,
  Proposal,
  ScopeChain,
  SelectionAnchor,
  SelectionInfo,
} from "./types";

// ── The editor handle (engine hidden behind plain shapes) ──
import { type CollabStatus, EditorCore, type EditorCoreConfig } from "./EditorCore";
import { DefaultLaneResolver, type LanePolicy } from "./lanes/LaneResolver";
import { acceptProposal, applyEdit } from "./transactions/apply";
import type {
  ApplyResult,
  BlockType,
  DocRange,
  EditIntent,
  InlineFormat,
  ListType,
  ObjectActionRequest,
  ParticipantRef,
  Proposal,
  SelectionAnchor,
  SelectionInfo,
} from "./types";

/** Live-collaboration params for the v2 room (plain strings only — no engine types). */
export interface EditorV2CollabConfig {
  url: string;
  documentId: string;
  /** A string, or a function read on every (re)connect (a short-lived, host-refreshed token). */
  token: string | (() => string);
  /**
   * Connection state for the v2 room, so a host can render an honest banner instead of an
   * editor that looks live while the socket is being refused (W6 / R7).
   */
  onStatus?(status: CollabStatus): void;
}

/** Re-exported so a host can type its own connection-state UI. */
export type { CollabStatus } from "./EditorCore";

export interface EditorV2Config {
  /**
   * Skin-supplied NodeViews (doc-widgets W4): the React skin renders `widget`/`chart` nodes
   * without the engine importing React. Engine-owned live-object views win on collision.
   */
  extraNodeViews?: Record<
    string,
    (node: unknown, view: unknown, getPos: () => number | undefined) => unknown
  >;
  seedStateBase64?: string;
  /** When set, bind to the `document-v2.<id>` collaboration room via the reused Hocuspocus spine. */
  collab?: EditorV2CollabConfig;
  /** Lane defaults down the containment chain, with a most-restrictive safety floor. */
  lanePolicy: LanePolicy;
  /**
   * The local human actor. Used for provisional stamping attribution on fresh typing,
   * `commitSelection()`'s `by`, and `insertObject` attribution.
   */
  localParticipant: ParticipantRef;
  /** Empty-doc placeholder text. */
  placeholder?: string;
  /** Stable timestamp source (injected so the engine stays deterministic/testable). */
  now(): string;
  /** Fires on commit to derive truth/decision cards from committed content (defaults to no-op). */
  deriver?: EditorCoreConfig["deriver"];
  onChange?: EditorCoreConfig["onChange"];
  onProposalsChanged?: EditorCoreConfig["onProposalsChanged"];
  /** Fires after every dispatched transaction (and once after mount) with a plain selection snapshot. */
  onSelectionChange?(info: SelectionInfo): void;
  /**
   * Fired ONLY by the 'Run with AI' affordance on a live-object card (task cards). Edit/done/
   * remove are handled internally by the engine's NodeViews via attributed transactions.
   */
  onObjectAction?(req: ObjectActionRequest): void;
}

/** The public handle. Every method takes/returns plain shapes — no engine types cross it. */
export interface EditorV2Handle {
  mount(host: HTMLElement): void;
  focus(): void;
  /** Drive ANY edit (human or AI) through the single choke point. */
  dispatchEdit(intent: EditIntent): ApplyResult;
  /** Accept a pending proposal — applies its stored payload (or `text` when given) as a normal attributed transaction. */
  acceptProposal(proposalId: string, text?: string): ApplyResult | null;
  rejectProposal(proposalId: string): void;
  /** Transition a range provisional→committed (may trigger the Deriver in the host). */
  commit(range: DocRange, by: string): void;
  /** Yjs-aware undo/redo (collab-safe; never rolls back remote peers' work). */
  undo(): boolean;
  redo(): boolean;
  toggleFormat(f: InlineFormat): void;
  /** Toggle semantics: setting the active block type reverts to paragraph; blockquote wraps/lifts. */
  setBlockType(b: BlockType): void;
  /** wrapInList when not in that list type; liftListItem when already in it. */
  toggleList(l: ListType): void;
  /** Commit the current selection as the local participant. False when empty/nothing to do. */
  commitSelection(): boolean;
  getSelectionInfo(): SelectionInfo;
  /**
   * The selection as a WRITER-BACK anchor: plain-text offsets and context windows alongside
   * the engine positions, or null when nothing is selected. `getSelectionInfo()` answers what
   * the chrome should look like; this answers where an apply-at-range should land and what a
   * stored document anchor should remember. `contextWindow` characters are taken either side.
   */
  getSelectionAnchor(contextWindow: number): SelectionAnchor | null;
  /**
   * Re-find a span by its TEXT — the range it occupies NOW, or null when the text is gone.
   * A host that stored a quotation (a comment thread's anchor) re-locates it by content, so a
   * position captured earlier can never overwrite the wrong words.
   */
  findText(text: string): DocRange | null;
  /** Insert a registered live-object block (task, reminder, …) after the current block, as a local act. */
  insertObject(kind: string, summary: string, data?: Record<string, unknown>): void;
  /**
   * Insert a `widget` block (doc-widgets §D6) after the current block, as a local act. `data` is
   * the widget payload (type/title/series|stat|table/source); `alt` keeps it visible to search
   * and screen readers wherever a host cannot render it.
   */
  insertWidget(data: Record<string, unknown>, alt: string): void;
  /** Patch a live object's summary/data by objectId (one attributed act transaction). False when not found. */
  updateObject(
    objectId: string,
    patch: { summary?: string; data?: Record<string, unknown> },
  ): boolean;
  /** Remove a live object by objectId (one attributed act transaction). False when not found. */
  removeObject(objectId: string): boolean;
  /**
   * Clamp `pos` to the document and snap it to a valid insertion point; returns the empty range
   * `{from: p, to: p}` — the insert-after helper for AI-result and whole-document prompt flows.
   */
  insertAt(pos: number): DocRange;
  /**
   * Fresh empty insert range immediately AFTER the live object, re-resolved against the CURRENT
   * doc (an insert-after position captured at click time goes stale as soon as anyone types).
   * Null when the object no longer exists.
   */
  insertAfterObject(objectId: string): DocRange | null;
  getContentJson(): unknown;
  getMarkdown(): string;
  getStateBase64(): string;
  getProposals(): Proposal[];
  /** Copy-forward a v1 PM-JSON document into this fresh v2 doc (opt-in). */
  seedFromPmJson(pmJson: unknown): void;
  destroy(): void;
}

/** Construct a v2 editor. The ProseMirror + Yjs engine is created and kept entirely inside. */
export function createEditorV2(config: EditorV2Config): EditorV2Handle {
  // Belt-and-braces: `now` is required by the type; default only against untyped callers.
  const now = config.now ?? (() => new Date().toISOString());
  const core = new EditorCore({
    localParticipant: config.localParticipant,
    seedStateBase64: config.seedStateBase64,
    collab: config.collab,
    placeholder: config.placeholder,
    now,
    deriver: config.deriver,
    onChange: config.onChange,
    onProposalsChanged: config.onProposalsChanged,
    onSelectionChange: config.onSelectionChange,
    onObjectAction: config.onObjectAction,
    extraNodeViews: config.extraNodeViews,
  });
  const resolver = new DefaultLaneResolver(config.lanePolicy);
  const deps = { engine: core, resolver, now };

  return {
    mount: (host) => core.mount(host),
    focus: () => core.focus(),
    dispatchEdit: (intent) => applyEdit(deps, intent),
    acceptProposal: (proposalId, text) => {
      const proposal = core.getProposals().find((p) => p.proposalId === proposalId);
      // Only PENDING proposals are acceptable — guards same-client double-apply and
      // accept-after-reject (cross-peer races converge via the CRDT status write below).
      if (!proposal || proposal.status !== "pending") return null;
      let result: ApplyResult;
      try {
        // apply.ts falls back to the proposal's stored payload when `text` is undefined.
        result = acceptProposal(deps, proposal, text);
      } catch {
        // Stale range (the doc shrank/shifted since the proposal was made) — the transaction
        // failed, so keep the proposal pending instead of silently losing its payload.
        return null;
      }
      // Flip status only AFTER the payload actually applied.
      core.setProposalStatus(proposalId, "accepted");
      return result;
    },
    rejectProposal: (proposalId) => core.setProposalStatus(proposalId, "rejected"),
    commit: (range, by) => core.commitRange(range, by, now()),
    undo: () => core.undo(),
    redo: () => core.redo(),
    toggleFormat: (f) => core.toggleFormat(f),
    setBlockType: (b) => core.setBlockType(b),
    toggleList: (l) => core.toggleList(l),
    commitSelection: () => core.commitSelection(),
    getSelectionInfo: () => core.getSelectionInfo(),
    getSelectionAnchor: (contextWindow) => core.getSelectionAnchor(contextWindow),
    findText: (text) => core.findText(text),
    insertWidget: (data, alt) =>
      core.insertWidgetAttributed(data, alt, {
        participant: config.localParticipant,
        lane: "act",
        ts: now(),
      }),
    insertObject: (kind, summary, data) =>
      core.insertObjectAttributed(kind, summary, data ?? {}, {
        participant: config.localParticipant,
        lane: "act",
        ts: now(),
      }),
    updateObject: (objectId, patch) => core.updateObject(objectId, patch),
    removeObject: (objectId) => core.removeObject(objectId),
    insertAt: (pos) => core.insertAt(pos),
    insertAfterObject: (objectId) => core.insertAfterObject(objectId),
    getContentJson: () => core.getContentJson(),
    getMarkdown: () => core.getMarkdown(),
    getStateBase64: () => core.getStateBase64(),
    getProposals: () => core.getProposals(),
    seedFromPmJson: (pmJson) => core.seedFromPmJson(pmJson),
    destroy: () => core.destroy(),
  };
}
